import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { almacen, descargar, type LotTrace, type Movement, type MovementKind } from '../api';
import { Buscable } from '../Buscable';
import { opcionDeInsumo } from '../busqueda';
import {
  Boton,
  Cabecera,
  Cargando,
  ConAyuda,
  Fallo,
  Filtro,
  Pastilla,
  Rotulo,
  Tarjeta,
  Vacio,
} from '../componentes';
import {
  MOTIVO_DE_MERMA,
  MOVIMIENTO,
  cantidad,
  cantidadConSigno,
  dia,
  diaDeLima,
  hora,
  soles,
} from '../formato';
import type { WasteReason } from '../api';
import { AjusteManual, Devolucion, Traspaso } from '../formularios';
import { fechaDeCalendario } from '../vencimiento';
import { mayuscula, useVocabulario, type Vocabulario } from '../vocabulario';

const TIPOS: { id: MovementKind | 'todos'; texto: string }[] = [
  { id: 'todos', texto: 'Todo movimiento' },
  { id: 'entry', texto: 'Entrada' },
  { id: 'sale', texto: 'Salida por venta' },
  { id: 'production', texto: 'Producción' },
  { id: 'waste', texto: 'Merma' },
  { id: 'transfer', texto: 'Traspaso' },
  { id: 'adjustment', texto: 'Ajuste' },
  { id: 'return', texto: 'Devolución' },
  { id: 'customer_return', texto: 'Devolución del cliente' },
];

/**
 * El libro: toda entrada y toda salida, con su papel y su usuario.
 *
 * Es la pantalla a la que se viene cuando el conteo no cuadra, así que enseña
 * el saldo de cada movimiento y no solo la cantidad: leyendo hacia abajo, la
 * cadena tiene que cerrar sola. Un salto es la respuesta a «¿desde cuándo?».
 *
 * Y es donde se rastrea un lote (`#/kardex/lote/<código>`): el día que el
 * laboratorio retira el AMX2405, lo que hay que saber es si queda, por dónde
 * salió y en qué ventas. Se escribe el código de la caja, no se elige de una
 * lista: el lote vendido entero es justo el que más importa encontrar, y ya
 * no sale entre los lotes con algo dentro.
 */
export function Kardex() {
  const vocabulario = useVocabulario();
  const [tipo, setTipo] = useState<MovementKind | 'todos'>('todos');

  /*
   * Desde y hasta, en días de Lima. Sin fechas la pantalla traía los últimos
   * doscientos movimientos y nada más: «¿qué pasó con el pulpo la semana
   * pasada?» no tenía respuesta si desde entonces se había movido mucho.
   */
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');

  /*
   * El insumo —o el lote— se elige y la dirección lo recuerda.
   *
   * La API aceptaba `supplyId` desde el primer día y ninguna pantalla lo
   * pedía: para responder «¿desde cuándo no cuadra el pulpo?» había que bajar
   * por el libro entero. Va en el `hash` para que se pueda llegar desde la
   * tabla de insumos —y desde Lotes, «Rastrear»— y para que la pestaña se
   * pueda dejar abierta en un insumo concreto, que es como se usa cuando algo
   * no cuadra.
   */
  const [elegido, setElegido] = useState(() => deLaUrl());
  const [loteEscrito, setLoteEscrito] = useState(() => deLaUrl().lote);

  useEffect(() => {
    const alCambiar = () => {
      const desdeLaUrl = deLaUrl();
      setElegido(desdeLaUrl);
      setLoteEscrito(desdeLaUrl.lote);
    };
    window.addEventListener('hashchange', alCambiar);

    return () => window.removeEventListener('hashchange', alCambiar);
  }, []);

  const insumoElegido = elegido.insumo;
  const loteBuscado = elegido.lote;

  const insumos = useQuery({ queryKey: ['insumos'], queryFn: almacen.supplies });

  const consulta = useQuery({
    queryKey: ['movimientos', tipo, insumoElegido, desde, hasta],
    queryFn: () =>
      almacen.movements({
        ...(tipo === 'todos' ? {} : { kind: tipo }),
        ...(insumoElegido === '' ? {} : { supplyId: insumoElegido }),
        ...(desde === '' ? {} : { from: desde }),
        ...(hasta === '' ? {} : { to: hasta }),
        limit: 500,
      }),
    enabled: loteBuscado === '',
  });

  const rastreo = useQuery({
    queryKey: ['rastreo-de-lote', loteBuscado],
    queryFn: () => almacen.traceLot(loteBuscado),
    enabled: loteBuscado !== '',
    retry: false,
  });

  const movimientos = consulta.data ?? [];
  const mirando = (insumos.data ?? []).find((i) => i.id === insumoElegido);

  const rastrear = (codigo: string) => {
    const limpio = codigo.trim();
    window.location.hash = limpio === '' ? '#/kardex' : `#/kardex/lote/${encodeURIComponent(limpio)}`;
    setElegido({ insumo: '', lote: limpio });
  };

  return (
    <>
      <Cabecera
        titulo="Kardex de movimientos"
        subtitulo="Toda entrada y salida con documento y usuario" ayuda="El libro del almacén: cada entrada y salida, con quién la hizo, cuándo y con qué papel. Es donde se mira cuando algo no cuadra, y donde se rastrea un lote."
        acciones={
          <>
            <Boton
              onClick={() => {
                /*
                 * Se exporta lo que se está mirando, con sus filtros puestos.
                 * Un botón que bajara siempre el libro entero obligaría a
                 * recortar en Excel justo lo que aquí ya está recortado.
                 */
                const query = new URLSearchParams();
                if (tipo !== 'todos') query.set('kind', tipo);
                if (insumoElegido !== '') query.set('supplyId', insumoElegido);
                if (desde !== '') query.set('from', desde);
                if (hasta !== '') query.set('to', hasta);
                const sufijo = query.toString();

                void descargar(
                  `/inventory/movements.csv${sufijo === '' ? '' : `?${sufijo}`}`,
                  `kardex-${diaDeLima()}.csv`,
                );
              }}
              disabled={loteBuscado !== '' || movimientos.length === 0}
            >
              Exportar
            </Boton>
            <Traspaso />
            <Devolucion />
            <AjusteManual />
          </>
        }
      />

      <div className="cuerpo">
        <form
          className="filtros"
          onSubmit={(evento) => {
            evento.preventDefault();
            rastrear(loteEscrito);
          }}
        >
          <div className="campo campo--en-linea">
            <Rotulo htmlFor="kardex-lote" ayuda="El número de lote que trae la caja, tal cual. Dice si queda, por dónde salió y en qué ventas: es lo que se pregunta el día que el fabricante retira un lote.">Rastrear lote</Rotulo>
            <input
              id="kardex-lote"
              placeholder="AMX2405"
              value={loteEscrito}
              onChange={(e) => setLoteEscrito(e.target.value)}
            />
          </div>
          <Boton variante="secundario" type="submit" disabled={loteEscrito.trim() === ''}>
            Rastrear
          </Boton>
          {loteBuscado !== '' ? (
            <Boton variante="secundario" onClick={() => rastrear('')}>
              Volver al libro
            </Boton>
          ) : null}
        </form>

        {loteBuscado !== '' ? (
          <RastreoDeLote
            codigo={loteBuscado}
            consulta={rastreo}
            vocabulario={vocabulario}
          />
        ) : (
          <>
            {consulta.isError ? (
              <Fallo error={consulta.error} onReintentar={() => consulta.refetch()} />
            ) : null}

            <div className="filtros">
              <div className="filtro-buscable">
                <Buscable
                  etiquetaAccesible={`Ver solo un ${vocabulario.insumo}`}
                  valor={insumoElegido}
                  onCambio={(id) => {
                    window.location.hash = id === '' ? '#/kardex' : `#/kardex/${id}`;
                    setElegido({ insumo: id, lote: '' });
                  }}
                  sinEleccion={`Todos los ${vocabulario.insumos}`}
                  placeholder={`Busca un ${vocabulario.insumo} por nombre o código…`}
                  opciones={(insumos.data ?? []).map((insumo) =>
                    opcionDeInsumo(insumo, `se cuenta en ${insumo.unit}`),
                  )}
                />
              </div>
              <div className="campo campo--en-linea">
                <Rotulo htmlFor="kardex-desde" ayuda="Ver solo desde este día. Vacío, desde el principio.">Del</Rotulo>
                <input
                  id="kardex-desde"
                  type="date"
                  value={desde}
                  max={hasta === '' ? diaDeLima() : hasta}
                  onChange={(e) => setDesde(e.target.value)}
                />
              </div>
              <div className="campo campo--en-linea">
                <Rotulo htmlFor="kardex-hasta" ayuda="Ver solo hasta este día, incluido. Vacío, hasta hoy.">al</Rotulo>
                <input
                  id="kardex-hasta"
                  type="date"
                  value={hasta}
                  min={desde}
                  max={diaDeLima()}
                  onChange={(e) => setHasta(e.target.value)}
                />
              </div>
              {TIPOS.map((opcion) => (
                <Filtro
                  key={opcion.id}
                  texto={opcion.texto}
                  activo={tipo === opcion.id}
                  onClick={() => setTipo(opcion.id)}
                />
              ))}
              <span className="recuento">
                {movimientos.length === 500 ? 'LOS ÚLTIMOS 500' : movimientos.length} MOVIMIENTOS
              </span>
            </div>

            {consulta.isLoading ? (
              <Cargando que="el kardex" />
            ) : movimientos.length === 0 ? (
              <Vacio
                titulo={
                  mirando === undefined
                    ? 'Sin movimientos'
                    : `${mirando.name} no tiene movimientos`
                }
                mensaje={
                  mirando === undefined
                    ? 'El libro está vacío. En cuanto entre o salga mercadería, cada movimiento queda aquí con quién lo hizo, cuándo y con qué papel detrás.'
                    : 'Nunca ha entrado ni salido de este local. Mientras no entre una vez, no tiene costo y lo que lo lleve saldrá más barato de lo real.'
                }
              />
            ) : (
              <TablaDeMovimientos movimientos={movimientos} vocabulario={vocabulario} />
            )}
          </>
        )}
      </div>
    </>
  );
}

/**
 * Lo que se sabe de un código de lote: dónde está, en qué ventas salió y su
 * kardex. Puede ser más de uno —el mismo número en dos insumos—, y cada uno
 * va por su lado.
 */
function RastreoDeLote({
  codigo,
  consulta,
  vocabulario,
}: {
  codigo: string;
  consulta: { isLoading: boolean; isError: boolean; error: unknown; data: LotTrace | undefined; refetch: () => unknown };
  vocabulario: Vocabulario;
}) {
  if (consulta.isLoading) return <Cargando que={`el lote ${codigo}`} />;

  if (consulta.isError) {
    const noExiste =
      typeof consulta.error === 'object' &&
      consulta.error !== null &&
      'status' in consulta.error &&
      (consulta.error as { status: number }).status === 404;

    return noExiste ? (
      <Vacio
        titulo={`No hay ningún lote ${codigo} en este local`}
        mensaje="Revisa el número tal cual viene en la caja. Si se recibió sin anotar el lote del fabricante, el almacén le puso uno con la fecha (L-AAAAMMDD-NN) y por este número no se encuentra."
      />
    ) : (
      <Fallo error={consulta.error} onReintentar={() => void consulta.refetch()} />
    );
  }

  const lotes = consulta.data?.lots ?? [];

  return (
    <>
      {lotes.map((lote) => (
        <Tarjeta key={lote.id}>
          <h2 className="tarjeta__titulo">
            {lote.supply} · lote <span className="mono">{lote.code}</span>
          </h2>
          <p className="nota">
            {lote.expiresAt === null
              ? 'Sin vencimiento'
              : `Vence el ${fechaDeCalendario(lote.expiresAt)} (${lote.expiryLabel})`}
            {' · '}entró el {dia(lote.receivedAt)}
            {lote.supplier === null ? '' : ` de ${lote.supplier}`}
            {' · '}
            {Number.parseFloat(lote.quantity) > 0
              ? `quedan ${cantidad(lote.quantity)} ${lote.unit} en el estante`
              : 'ya no queda nada en el estante'}
          </p>

          {lote.sales.length === 0 ? (
            <p className="nota">No salió en ninguna venta.</p>
          ) : (
            <div className="tabla-envoltura">
              <table>
                <thead>
                  <tr>
                    <th>Cuándo</th>
                    <th>Producto</th>
                    <th className="numero">Cantidad</th>
                    <th>Pedido</th>
                    <th><ConAyuda ayuda="La boleta o factura del pedido. Vacío si se vendió sin comprobante.">Comprobante</ConAyuda></th>
                    <th><ConAyuda ayuda="A quién se le vendió, si la venta lo anotó. Solo lo ve quien puede ver los clientes.">Cliente</ConAyuda></th>
                  </tr>
                </thead>
                <tbody>
                  {lote.sales.map((venta) => (
                    <tr key={venta.movementId}>
                      <td className="mono apagado">
                        {dia(venta.occurredAt)} {hora(venta.occurredAt)}
                      </td>
                      <td>
                        {venta.product}{' '}
                        {venta.cancelled ? <Pastilla texto="anulado" tono="neutro" /> : null}
                      </td>
                      <td className="numero">
                        {cantidad(venta.quantity)} {lote.unit}
                      </td>
                      <td className="mono apagado">
                        {venta.orderNumber === null ? '—' : `N.º ${venta.orderNumber}`}
                      </td>
                      <td className="mono">{venta.receipts.length === 0 ? '—' : venta.receipts.join(', ')}</td>
                      <td>
                        {venta.customer === null
                          ? '—'
                          : [venta.customer.name, venta.customer.documentNumber, venta.customer.phone]
                              .filter((dato) => dato !== null && dato !== '')
                              .join(' · ')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <h3 className="formulario__subtitulo">Su kardex</h3>
          <TablaDeMovimientos movimientos={lote.movements} vocabulario={vocabulario} />
        </Tarjeta>
      ))}
    </>
  );
}

function TablaDeMovimientos({
  movimientos,
  vocabulario,
}: {
  movimientos: Movement[];
  vocabulario: Vocabulario;
}) {
  return (
    <div className="tabla-envoltura">
      <table>
        <thead>
          <tr>
            <th>Cuándo</th>
            <th>Tipo</th>
            <th>{mayuscula(vocabulario.insumo)}</th>
            <th className="numero"><ConAyuda ayuda="Lo que entró (+) o salió (−) con este movimiento.">Cantidad</ConAyuda></th>
            <th className="numero"><ConAyuda ayuda="Lo que quedó después de este movimiento. Leyendo hacia abajo, cada saldo sale del anterior.">Saldo</ConAyuda></th>
            <th className="numero"><ConAyuda ayuda="Lo que vale el movimiento, al costo de ese momento.">Valor</ConAyuda></th>
            <th><ConAyuda ayuda="El papel detrás: la guía, el número de conteo, la orden.">Documento</ConAyuda></th>
            <th><ConAyuda ayuda="Quién lo registró. «Automático» es el sistema descontando una venta.">Usuario</ConAyuda></th>
          </tr>
        </thead>
        <tbody>
          {movimientos.map((movimiento) => {
            const clase = MOVIMIENTO[movimiento.kind];
            const negativo = movimiento.quantity.startsWith('-');

            return (
              <tr key={movimiento.id}>
                <td className="mono apagado">
                  {dia(movimiento.occurredAt)} {hora(movimiento.occurredAt)}
                  {/*
                   * La venta que la caja hizo sin red entra al libro cuando
                   * vuelve la conexión. Sin la hora de verdad, el pollo vendido
                   * a las 13:10 parecía salir a las 18:40 y no cuadraba con la
                   * comanda.
                   */}
                  {movimiento.soldAt ? (
                    <>
                      <br />
                      vendido{' '}
                      {dia(movimiento.soldAt) === dia(movimiento.occurredAt) ? '' : `${dia(movimiento.soldAt)} `}
                      {hora(movimiento.soldAt)}
                    </>
                  ) : null}
                </td>
                <td>
                  <Pastilla texto={clase.texto} tono={clase.tono} />
                </td>
                <td>
                  <div className="celda-nombre__texto">
                    <strong>{movimiento.supply}</strong>
                    <span>{describir(movimiento.reason, movimiento.lot)}</span>
                  </div>
                </td>
                <td className={`numero ${negativo ? 'negativo' : 'positivo'}`}>
                  {cantidadConSigno(movimiento.quantity)} {movimiento.unit}
                </td>
                <td className="numero">
                  {cantidad(movimiento.balance)} {movimiento.unit}
                </td>
                <td className="numero apagado">{soles(movimiento.value)}</td>
                <td className="mono apagado">{movimiento.document ?? '—'}</td>
                <td className="apagado">
                  {/*
                   * Nulo es el servidor descontando por su cuenta al
                   * confirmarse un pedido. Se dice, en vez de dejar la
                   * celda vacía: un hueco hace pensar que se perdió el
                   * dato, y aquí significa algo.
                   */}
                  {movimiento.performedBy ?? 'Automático'}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/**
 * `#/kardex/<id>`, `#/kardex/lote/<código>` o nada. La sección sola
 * significa «todos».
 */
function deLaUrl(): { insumo: string; lote: string } {
  const resto = window.location.hash.replace('#/kardex', '').replace(/^\//, '');

  if (resto.startsWith('lote/')) {
    return { insumo: '', lote: decodeURIComponent(resto.slice('lote/'.length)) };
  }

  return { insumo: resto, lote: '' };
}

/** El motivo en palabras, o el lote, o nada. Lo que haya. */
function describir(reason: string | null, lote: string | null): string {
  if (reason !== null) {
    return MOTIVO_DE_MERMA[reason as WasteReason] ?? reason;
  }

  return lote === null ? '' : `Lote ${lote}`;
}
